<template>
  <div
    :class="[
      'poster-wrapper',
      { 'has-detail': hasDetail },
      { 'active': active },
      { 'disabled': disabled }
    ]">
    <div
      v-if="alternativeText && !showPlay"
      class="sr-only"
      role="heading">
      {{ alternativeText }}
    </div>
    <section
      :class="[
        'wptv-poster',
        { 'no-image': noImage },
        { 'show-gradient': showGradient }
      ]">
      <div
        :class="[
          'cover',
          {
            'error': hasFailed,
            'full-gradient': hasDetail,
            'csa-gradient': csaEnabled
          }
        ]">
        <wptv-loader
          :show="isLoading"
          :height="40"
          :width="40"
          color="white"
          class="is-loading" />

        <poster-image
          class="poster-image"
          :ratio="ratio"
          :image-data="imageData"
          :alt-image="altImage"
          :has-failed="hasFailed"
          :is-loading="isLoading"
          :is-aria-hidden="isAriaHidden"
          @load-success="onLoadSuccess"
          @load-error="onLoadError" />
        <poster-logo
          v-if="showLogo"
          :visibility="logoVisibility"
          :alt-logo="altLogo"
          :logo="logo"
          :centered="!hasDetail && noImage" />
        <poster-subscribe
          v-if="showSubscribe"
          :visibility="subscribeVisibility" />
        <poster-play
          v-if="showPlay"
          :visibility="playVisibility"
          :alt-play="alternativeText || altPlay"
          :device-visibility="playDeviceVisibility"
          :play-size="playSize"
          :is-loading="isLoading" />
        <poster-csa
          v-if="csaEnabled"
          :csa="csa"
          :is-progress-bar="showProgress"
          :cover-size-class="getCoverSizeClass" />
        <poster-zapping-number
          v-if="showZappingNumber"
          :visibility="zappingNumberVisibility"
          :zapping-number="zappingNumber" />
        <poster-actions
          v-if="actions.length !== 0"
          class="poster-actions"
          :actions="actions" />
        <div
          v-if="recordingInProgress"
          aria-hidden="true"
          :class="['recording-icon', `${getCoverSizeClass}-recording`]">
          <wptv-icon name="record" />
        </div>
        <div
          v-if="recordingProgrammed"
          aria-hidden="true"
          :class="['recording-icon', `${getCoverSizeClass}-recording`]">
          <wptv-icon name="record-programmed" />
        </div>
        <slot name="thumbnails" />
      </div>
    </section>

    <wptv-progress-bar-legacy
      v-if="showProgress && progressValue >= 0"
      :cover-size-class="getCoverSizeClass"
      :disabled="progressDisabled"
      :progress-value="progressValue"
      data-wptv="timeline-program" />

    <div
      v-if="hasDetail"
      :class="['poster-detail', {'available': !isLoading }]">
      <slot />
    </div>
  </div>
</template>

<script>
import WptvPosterConfig from './poster.config'
import { WptvLoader } from '../loader'
import PosterImage from './posterImage/PosterImage'
import PosterCsa from './posterCsa/PosterCsa'
import PosterSubscribe from './posterSubscribe/PosterSubscribe'
import PosterPlay from './posterPlay/PosterPlay'
import PosterLogo from './posterLogo/PosterLogo'
import PosterZappingNumber from './posterZappingNumber/PosterZappingNumber'
import PosterActions from './posterActions/PosterActions'
import WptvProgressBarLegacy from '../progressBarLegacy/ProgressBar'
import WptvIcon from '../icon/Icon'

export default {
  name: 'ItemPoster',
  components: {
    WptvProgressBarLegacy,
    PosterZappingNumber,
    PosterLogo,
    PosterPlay,
    PosterSubscribe,
    PosterCsa,
    PosterImage,
    PosterActions,
    WptvLoader,
    WptvIcon
  },
  props: {
    ratio: {
      type: String,
      default: WptvPosterConfig.RATIO.RATIO_16_9
    },
    showGradient: {
      type: Boolean,
      default: true
    },
    actions: {
      type: Array,
      default: () => []
    },
    // Logo
    showLogo: {
      type: Boolean,
      default: true
    },
    logoVisibility: {
      type: String,
      default: ''
    },
    // Subscribe
    showSubscribe: {
      type: Boolean,
      default: true
    },
    subscribeVisibility: {
      type: String,
      default: ''
    },
    // Play
    showPlay: {
      type: Boolean,
      default: false
    },
    playVisibility: {
      type: String,
      default: ''
    },
    playDeviceVisibility: {
      type: String,
      default: WptvPosterConfig.PLAY_VISIBILITY.DESKTOP
    },
    playSize: {
      type: Number,
      default: 40
    },
    // CSA
    showCsa: {
      type: Boolean,
      default: true
    },
    // Progress
    showProgress: {
      type: Boolean,
      default: true
    },
    // Zapping Number
    showZappingNumber: {
      type: Boolean,
      default: false
    },
    zappingNumberVisibility: {
      type: String,
      default: ''
    },
    // Values
    imageData: {
      type: Object,
      required: true
    },
    logo: {
      type: String,
      default: ''
    },
    csa: {
      type: Number,
      default: 1
    },
    altImage: {
      type: String,
      default: ''
    },
    altLogo: {
      type: String,
      default: ''
    },
    altPlay: {
      type: String,
      default: ''
    },
    alternativeText: {
      type: String,
      default: ''
    },
    progressValue: {
      type: Number,
      default: 0
    },
    zappingNumber: {
      type: Number,
      default: 1
    },
    // States
    active: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isAriaHidden: {
      type: Boolean,
      default: false
    },
    progressDisabled: {
      type: Boolean,
      default: false
    },
    recordingInProgress: {
      type: Boolean,
      default: false
    },
    recordingProgrammed: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      coverWidth: 0,
      isLoading: true,
      hasFailed: false,
      noImage: false
    }
  },
  computed: {
    hasDetail () {
      return !!this.$slots.default && !!this.$slots.default[0]
    },
    csaEnabled () {
      return this.showCsa && this.csa > 1 && this.csa < 6
    },
    getCoverSizeClass () {
      // this is used to place and size subelements depending of the size of the cover
      if (this.coverWidth < 150) {
        return 'tiny'
      } else if (this.coverWidth < 270) {
        return 'medium'
      }
      return 'large'
    }
  },
  mounted () {
    // used to tell the subelements to change their css depending of the size of the cover
    const resizeObserver = new ResizeObserver(entries => {
      this.coverWidth = entries[0].contentRect.width
    })
    resizeObserver.observe(this.$el.getElementsByClassName('cover')[0])
  },
  methods: {
    onLoadSuccess (noImage) {
      this.isLoading = false
      this.noImage = noImage
    },
    onLoadError () {
      this.isLoading = false
      this.hasFailed = true
      this.noImage = true
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../scss/abstracts/variables';
  @import '../../scss/abstracts/mixins';
  @import 'poster-gradients';

  .poster-wrapper {
    position: relative;
    background: $black;

    &.has-detail {
      @include mediaQueries('min-height', (small: 285px));

      .wptv-poster .cover {
        @include mediaQueries('max-height', (xxsmall: 180px, xsmall: 380px));
      }
    }

    &.active {
      .poster-actions {
        @include mediaQueries('display', (large: flex ,xlarge: none));
      }
      cursor: pointer;

      &:focus .cover,
      &:hover .cover {
        .poster-actions {
          display: flex;
        }
        ::v-deep .image {
          filter: brightness(70%);
        }
      }
    }

    &:focus .cover,
    &:hover .cover {
      ::v-deep .poster-play.available {
        opacity: 1;
      }
    }

    // --------------------------------------------------------------------

    .wptv-poster {
      position: relative;
      width: 100%;

      .cover {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        display: flex;
        align-items: center;
        background: $textlightgrey;

        &.error {
          display: flex;
          justify-content: center;
          align-content: center;
        }

        .is-loading {
          position: absolute;
          margin: 0;
          left: 50%;
          top: 50%;
          transform: translate(-50%,-50%);
        }
      }
    }

    // --------------------------------------------------------------------

    .poster-detail {
      @include mediaQueries('position', (xxsmall: relative, medium: absolute));
      @include mediaQueries('margin-top', (xxsmall: -110px, medium: 0));
      bottom: 0;
      left: 0;
      width: 100%;
      z-index: 2;
    }

    .recording-icon {
      right: 15px;
      top: 15px;
      font-size: 24px;
      &.tiny-recording {
        right: 5px;
        top: 5px;
        font-size: 12px;
      }
      &.medium-recording {
        right: 10px;
        top: 10px;
        font-size: 18px;
      }
      position: absolute;
      z-index: 1;
    }
  }
</style>
